/**
 * ReputationBadge Component
 * =========================
 * Displays user or organization reputation score as a badge.
 * 
 * This is a MOAT FEATURE - drives trust and engagement.
 * 
 * Usage:
 *   <ReputationBadge userId="..." type="student" />
 *   <ReputationBadge orgId="..." type="organization" />
 *   <ReputationBadge score={75} tier="excellent" /> // Direct props
 */

import { useState, useEffect } from 'react';
import { Trophy, Star, Shield, Sparkles, Leaf } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Tier configurations
const TIER_CONFIG = {
  legendary: {
    icon: Trophy,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
    borderColor: 'border-amber-500/30',
    label: 'Legendary',
    gradient: 'from-amber-500 to-orange-500',
  },
  excellent: {
    icon: Star,
    color: 'text-violet-400',
    bgColor: 'bg-violet-500/20',
    borderColor: 'border-violet-500/30',
    label: 'Excellent',
    gradient: 'from-violet-500 to-purple-500',
  },
  good: {
    icon: Shield,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    borderColor: 'border-cyan-500/30',
    label: 'Good',
    gradient: 'from-cyan-500 to-blue-500',
  },
  average: {
    icon: Sparkles,
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/20',
    borderColor: 'border-slate-500/30',
    label: 'Average',
    gradient: 'from-slate-500 to-gray-500',
  },
  newcomer: {
    icon: Leaf,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
    label: 'Newcomer',
    gradient: 'from-emerald-500 to-green-500',
  },
};

// Hook to fetch reputation score
export const useReputationScore = (entityId, entityType, session) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!entityId || !session?.access_token) {
      setLoading(false);
      return;
    }

    const fetchScore = async () => {
      try {
        const endpoint = entityType === 'student' 
          ? `/api/reputation/student/${entityId}/quick`
          : `/api/reputation/organization/${entityId}/quick`;
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
          {
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchScore();
  }, [entityId, entityType, session?.access_token]);

  return { data, loading, error };
};

// Main Badge Component
export const ReputationBadge = ({
  // Direct props (if score is already known)
  score: propScore,
  tier: propTier,
  // Fetch props (fetch score from API)
  userId,
  orgId,
  type = 'student',
  session,
  // Display options
  size = 'md', // sm, md, lg
  showScore = true,
  showLabel = false,
  variant = 'default', // default, minimal, full
  className,
}) => {
  // Use hook if fetching is needed
  const entityId = userId || orgId;
  const entityType = userId ? 'student' : 'organization';
  const { data: fetchedData, loading } = useReputationScore(
    propScore === undefined ? entityId : null, 
    entityType, 
    session
  );
  
  // Determine score and tier
  const score = propScore ?? fetchedData?.score ?? 0;
  const tier = propTier ?? fetchedData?.tier ?? 'newcomer';
  const config = TIER_CONFIG[tier] || TIER_CONFIG.newcomer;
  const Icon = config.icon;

  // Size configurations
  const sizeConfig = {
    sm: { badge: 'h-6 px-2 text-xs', icon: 'w-3 h-3', score: 'text-xs' },
    md: { badge: 'h-8 px-3 text-sm', icon: 'w-4 h-4', score: 'text-sm' },
    lg: { badge: 'h-10 px-4 text-base', icon: 'w-5 h-5', score: 'text-base' },
  };
  const sizes = sizeConfig[size] || sizeConfig.md;

  if (loading) {
    return (
      <div className={cn(
        'animate-pulse rounded-full',
        sizes.badge,
        'bg-white/10',
        className
      )} />
    );
  }

  // Minimal variant - just icon
  if (variant === 'minimal') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn('flex items-center', className)}>
              <Icon className={cn(sizes.icon, config.color)} />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{config.label} • {Math.round(score)} rep</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Full variant - expanded display
  if (variant === 'full') {
    return (
      <div className={cn(
        'flex items-center gap-3 p-3 rounded-xl border',
        config.bgColor,
        config.borderColor,
        className
      )}
      data-testid="reputation-badge-full"
      >
        <div className={cn(
          'flex items-center justify-center w-10 h-10 rounded-full',
          `bg-gradient-to-br ${config.gradient}`
        )}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className={cn('font-semibold', config.color)}>
              {config.label}
            </span>
            <span className="text-white/60 text-sm">
              {Math.round(score)} rep
            </span>
          </div>
          <p className="text-xs text-white/40">Reputation Score</p>
        </div>
      </div>
    );
  }

  // Default variant - compact badge
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border',
              sizes.badge,
              config.bgColor,
              config.borderColor,
              className
            )}
            data-testid="reputation-badge"
          >
            <Icon className={cn(sizes.icon, config.color)} />
            {showScore && (
              <span className={cn(sizes.score, 'font-medium text-white/80')}>
                {Math.round(score)}
              </span>
            )}
            {showLabel && (
              <span className={cn(sizes.score, config.color)}>
                {config.label}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{config.label} Reputation • Score: {Math.round(score)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Score breakdown component for detailed views
export const ReputationBreakdown = ({ components, weights, className }) => {
  if (!components || !weights) return null;
  
  const factors = Object.entries(components).map(([key, data]) => ({
    name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    score: data.score || 0,
    weight: (weights[key] || 0) * 100,
    details: data.details || {},
  }));
  
  return (
    <div className={cn('space-y-3', className)} data-testid="reputation-breakdown">
      {factors.map((factor) => (
        <div key={factor.name} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-white/70">{factor.name}</span>
            <span className="text-white/50">
              {Math.round(factor.score)} × {factor.weight}%
            </span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-500 to-violet-500 rounded-full transition-all duration-500"
              style={{ width: `${factor.score}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReputationBadge;
